import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PhoneInput from "react-phone-number-input";
import {
  getCountryCallingCode,
  isPossiblePhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

const pageProps = JSON.parse(
  (document.querySelector("#app") as HTMLElement).dataset.page!
).props;

export interface PhoneInputComponentProps {
  value: any[];
  onChange: (values: any[]) => void;
  onCountryChange?: (country?: string) => void;
  helper?: React.ReactNode;
  placeholder?: string;
}

interface CountrySelectOption {
  value?: string;
  label: string;
  divider?: boolean;
}

interface CountrySelectProps {
  value?: string;
  onChange: (value?: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  options: CountrySelectOption[];
  iconComponent: React.ComponentType<any>;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

function formatPhoneInputValue(value: any) {
  if (typeof value !== "string" || value === "" || value.startsWith("+")) {
    return value;
  }

  return `+${value}`;
}

export function getPastedPhoneValue(value: string, country?: string) {
  const hasInternationalPrefix = value.trim().startsWith("+");
  const pastedDigits = value.replace(/\D/g, "");

  if (!pastedDigits) {
    return null;
  }

  const pastedPhoneNumber = parsePhoneNumberFromString(`+${pastedDigits}`);
  const hasCountryCallingCode = isPossiblePhoneNumber(`+${pastedDigits}`);

  if (!country) {
    return hasInternationalPrefix || hasCountryCallingCode
      ? pastedPhoneNumber?.number || value
      : null;
  }

  try {
    const selectedCountryCallingCode = getCountryCallingCode(country as any);

    if (pastedDigits.startsWith(selectedCountryCallingCode)) {
      return `+${selectedCountryCallingCode}${pastedDigits.slice(
        selectedCountryCallingCode.length
      )}`;
    }
  } catch {
    return hasInternationalPrefix || hasCountryCallingCode
      ? pastedPhoneNumber?.number || value
      : null;
  }

  return hasInternationalPrefix || hasCountryCallingCode
    ? pastedPhoneNumber?.number || value
    : null;
}

export function getCountryOptionLabel(option: CountrySelectOption) {
  if (!option.value) {
    return option.label;
  }

  try {
    return `${option.label} +${getCountryCallingCode(option.value as any)}`;
  } catch {
    return option.label;
  }
}

function SearchableCountrySelect({
  value,
  onChange,
  onFocus,
  onBlur,
  options,
  iconComponent: Icon,
  disabled,
  readOnly,
}: CountrySelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuSearch, setMenuSearch] = useState("");
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<React.CSSProperties>({});
  const countryOptions = options.filter((option) => !option.divider);
  const selectedOption =
    countryOptions.find((option) => option.value === value) || null;
  const filteredCountryOptions = countryOptions.filter((option) =>
    getCountryOptionLabel(option)
      .toLowerCase()
      .includes(menuSearch.toLowerCase())
  );

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    searchInputRef.current?.focus();

    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !wrapperRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsMenuOpen(false);
        setMenuSearch("");
        onBlur?.();
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, [isMenuOpen, onBlur]);

  useLayoutEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const updateMenuPosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      setMenuPosition({
        left: rect.left,
        position: "fixed",
        top: rect.bottom + 4,
        zIndex: 9999,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setActiveOptionIndex(0);
  }, [menuSearch]);

  useEffect(() => {
    if (activeOptionIndex >= filteredCountryOptions.length) {
      setActiveOptionIndex(Math.max(filteredCountryOptions.length - 1, 0));
    }
  }, [activeOptionIndex, filteredCountryOptions.length]);

  const toggleMenu = () => {
    if (disabled || readOnly) {
      return;
    }

    setIsMenuOpen((isOpen) => {
      const nextIsOpen = !isOpen;

      if (nextIsOpen) {
        onFocus?.();
      } else {
        setMenuSearch("");
        setActiveOptionIndex(0);
        onBlur?.();
      }

      return nextIsOpen;
    });
  };

  const selectCountry = (country?: string) => {
    onChange(country);
    setIsMenuOpen(false);
    setMenuSearch("");
    setActiveOptionIndex(0);
    onBlur?.();
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setMenuSearch("");
    setActiveOptionIndex(0);
    onBlur?.();
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Escape") {
      closeMenu();
      return;
    }

    if (!filteredCountryOptions.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveOptionIndex((index) =>
        Math.min(index + 1, filteredCountryOptions.length - 1)
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveOptionIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      selectCountry(filteredCountryOptions[activeOptionIndex].value);
    }
  };

  return (
    <div className="PhoneInputCountry" ref={wrapperRef}>
      <button
        type="button"
        className="phone-input-country-select__button"
        disabled={disabled || readOnly}
        aria-haspopup="listbox"
        aria-expanded={isMenuOpen}
        onClick={toggleMenu}
      >
        <Icon country={value} label={selectedOption?.label} />
        <span className="PhoneInputCountrySelectArrow" />
      </button>
      {isMenuOpen
        ? createPortal(
            <div
              className="phone-input-country-select__menu"
              ref={menuRef}
              style={menuPosition}
            >
          <div className="phone-input-country-select__menu-search-wrapper">
            <input
              ref={searchInputRef}
              className="form-control phone-input-country-select__menu-search"
              placeholder="Search countries"
              value={menuSearch}
              onChange={(event) => setMenuSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <div className="phone-input-country-select__options" role="listbox">
            {filteredCountryOptions.map((option, index) => (
              <button
                key={option.value || option.label}
                type="button"
                className="phone-input-country-select__option"
                role="option"
                aria-selected={index === activeOptionIndex}
                onClick={() => selectCountry(option.value)}
              >
                {getCountryOptionLabel(option)}
              </button>
            ))}
          </div>
        </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default function PhoneInputComponent({
  value,
  onChange,
  onCountryChange,
  helper,
  placeholder = "Enter a value",
}: PhoneInputComponentProps) {
  const defaultCountry = pageProps.defaultCountry.country_code || "GB";
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const phoneValue: string = formatPhoneInputValue(value[0] ?? "");
  const isValid = !!phoneValue && isPossiblePhoneNumber(phoneValue);

  const handleCountryChange = (country?: string) => {
    setSelectedCountry(country || defaultCountry);
    onCountryChange?.(country);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedPhoneValue = getPastedPhoneValue(
      event.clipboardData.getData("text"),
      selectedCountry
    );

    if (!pastedPhoneValue) {
      return;
    }

    event.preventDefault();
    onChange([pastedPhoneValue]);
  };

  return (
    <div className="phone-input-with-helper">
      <PhoneInput
        className="w-100 phone-input-with-helper__control"
        placeholder={placeholder}
        countrySelectComponent={SearchableCountrySelect}
        numberInputProps={{
          className: `form-control ${isValid ? "" : "border-secondary-subtle"}`,
          title: isValid ? "Number is valid" : "Number is not valid",
          onPaste: handlePaste,
        }}
        defaultCountry={defaultCountry}
        international
        withCountryCallingCode
        value={phoneValue}
        onChange={(val: string | undefined) => onChange([val])}
        onCountryChange={handleCountryChange}
      />
      {helper ? (
        <div className="phone-input-with-helper__helper">{helper}</div>
      ) : null}
    </div>
  );
}
